import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { Button } from '@iq/react-components';
import {
  resetNewlyCreatedProjectId,
  selectNewlyCreatedProjectId,
  getProjects,
} from '../../bundles/projects';
import FileUpload from '../FileUpload';
import ProjectCreateModal from './components/ProjectCreateModal/ProjectCreateModal';
import { PROJECT_DELIVERY_TYPE } from '../../constants';
import { isEnergyConnectMimsProject, cleanFormData } from '../../utils';

const ProjectCreateUpdateView = React.memo(
  ({
    siteParams = {},
    title,
    onSave,
    showButtonDownload = true,
    createUpdateButtonText,
    project,
  }) => {
    const { integrationInfo = 'Identiq' } = siteParams;
    const { isIntegration = false } = integrationInfo;
    const [displayAddSiteModal, setDisplayAddSiteModal] = useState(isIntegration);

    const [zipFile, setZipFile] = useState('');
    const [zipFileName, setZipFileName] = useState('');
    const newlyCreatedProjectId = useSelector(selectNewlyCreatedProjectId);
    const [redirect, setRedirect] = useState();
    const [formErrors, setFormErrors] = useState([]);
    const allProjects = useSelector(getProjects);

    const dispatch = useDispatch();

    const transformErrors = (errors) => {
      return errors.map((error) => {
        /* eslint-disable no-return-assign, no-param-reassign */
        switch (error.name) {
          case 'format':
            if (error.property === '.ip')
              error.message = 'Should be in format "ipv4" ex. "111.224.2.11"';
            if (['.moduleId', '.tenantId', '.edgeId', '.projectId'].includes(error.property))
              error.message =
                'Should be in format "uuid" ex. "308886e4-7e4f-4925-bd44-d72a6e6601fe" ';
            break;
          case 'required':
            error.message = `Enter ${error.property.charAt(0).toUpperCase() + error.property.slice(1)}`;
            break;

          default:
        }
        /* eslint-enable no-return-assign, no-param-reassign */
        return error;
      });
    };

    const onClose = useCallback(() => {
      setDisplayAddSiteModal(false);
      setZipFile('');
      setZipFileName('');
      setFormErrors([]);
    }, []);

    const handleCreateProject = (projectFromForm) => {
      const projectData = {
        ...projectFromForm,
        fileData: zipFile,
      };

      onSave(projectData);
      onClose();
    };

    const convertBase64 = (file) => {
      return new Promise((resolve, reject) => {
        const fileReader = new FileReader();
        fileReader.readAsDataURL(file);

        fileReader.onload = () => {
          resolve(fileReader.result);
        };
        fileReader.onerror = (error) => {
          reject(new Error(error));
        };
      });
    };

    const onDrop = useCallback(async (files) => {
      const file = files[0];
      const base64 = await convertBase64(file);
      setZipFile(base64.split('base64,')[1]);
      setZipFileName(file.name);
    }, []);

    const fileUrl = '/files/MappingExcel.xlsx';

    useEffect(() => {
      if (!newlyCreatedProjectId) return;
      const id = newlyCreatedProjectId;

      dispatch(resetNewlyCreatedProjectId());
      setRedirect(
        <Redirect
          push={true}
          to={`/mach/${id}/`}
        />
      );
    }, [newlyCreatedProjectId]);

    const formModel = useMemo(() => {
      if (!project) return null;

      const formObject = {
        name: project.name,
        ip: project.ip,
        deliveryType: isEnergyConnectMimsProject(project)
          ? PROJECT_DELIVERY_TYPE.EC
          : PROJECT_DELIVERY_TYPE.MIMS,
        // ec model
        environment: project.ecModel?.environment,
        moduleId: project.ecModel?.moduleId,
        tenantId: project.ecModel?.tenantId,
        projectId: project.ecModel?.projectId,
        edgeId: project.ecModel?.edgeId,
        typeDefAPI: project.ecModel?.typeDefAPI,
        dataAccessAPI: project.ecModel?.dataAccessAPI,
        deviceTwinAPI: project.ecModel?.deviceTwinAPI,
      };

      // Keys with empty values causes the form schema default values
      // to not be forwarded on save, even if they show up in the form.
      // The solution is to remove keys with empty values
      return cleanFormData(formObject);
    }, [project]);

    if (redirect) {
      return redirect;
    }

    const customValidate = (formData, errors) => {
      if (formData.name && allProjects) {
        const isDuplicateName = allProjects.some(
          (existingProject) =>
            existingProject.name.toLowerCase() === formData.name.toLowerCase() &&
            (!project || existingProject.id !== project.id)
        );

        if (isDuplicateName) {
          errors.name.addError('Name already exists');
        }
      }

      if (formErrors && formErrors.length > 0) {
        formErrors.forEach((error) => {
          const fieldName = error.property.startsWith('.')
            ? error.property.slice(1)
            : error.property;

          if (fieldName && errors[fieldName]) {
            errors[fieldName].addError(error.message);
          }
        });
      }

      return errors;
    };

    return (
      <div className="site-create-view-component">
        <a href={fileUrl}>
          {showButtonDownload && (
            <Button
              type="button"
              activity="primary"
              style={{ marginRight: '0.5rem' }}
            >
              Download config
            </Button>
          )}
        </a>

        <Button
          type="button"
          activity="primary"
          onClick={() => setDisplayAddSiteModal(true)}
        >
          {createUpdateButtonText || '+ Add Project'}
        </Button>

        {displayAddSiteModal && (
          <ProjectCreateModal
            title={title}
            projectData={formModel}
            siteParams={siteParams}
            onSubmit={handleCreateProject}
            transformErrors={transformErrors}
            onCloseModal={onClose}
            updateMode={Boolean(project)}
            errors={formErrors}
            customValidate={customValidate}
          >
            <FileUpload
              fileName={zipFileName}
              onDrop={onDrop}
            />
          </ProjectCreateModal>
        )}
      </div>
    );
  }
);

export default ProjectCreateUpdateView;
